/**
 * M26 — 건별 자동 검수 상태 배지 (RecordTable 의 검수 칸에서 사용).
 */

import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock,
  Eye,
  Loader2,
  Pencil,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { formatDateTime } from "@/features/jobs/format";
import { cn } from "@/lib/cn";
import type { AutomationState, AutomationStatus } from "@/lib/api/jobs";

import { autoFixChanges } from "./automationStatusUtil";
import { FIELD_META_MAP, type FieldMeta } from "./fieldLabels";

const STYLES: Record<AutomationStatus, { cls: string; icon: typeof Circle; spin?: boolean }> = {
  queued: { cls: "border-slate-200 bg-white text-slate-500", icon: Clock },
  running: { cls: "border-violet-200 bg-violet-50 text-violet-700", icon: Loader2, spin: true },
  auto_passed: { cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  auto_fixed: { cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: Sparkles },
  needs_human: { cls: "border-amber-300 bg-amber-50 text-amber-800", icon: AlertTriangle },
  human_owned: { cls: "border-slate-200 bg-slate-50 text-slate-500", icon: Pencil },
  skipped: { cls: "border-slate-200 bg-slate-50 text-slate-400", icon: Circle },
  error: { cls: "border-red-200 bg-red-50 text-red-700", icon: XCircle },
};

const BADGE_CLS =
  "inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-medium";

interface Props {
  automation: AutomationState | null | undefined;
  className?: string;
}

/** 자동 검수 상태 배지. 마우스를 올리면 사람 확인·제외 이유를 보여준다. */
export default function AutomationBadge({ automation, className }: Props) {
  const { t } = useTranslation();
  if (!automation) return null;
  if (automation.status === "auto_fixed") {
    return <AutoFixBadge automation={automation} className={className} />;
  }

  const { cls, icon: Icon, spin } = STYLES[automation.status] ?? STYLES.error;
  const label = t(`records.automation.status.${automation.status}`);
  const reason = automation.reason
    ? t(`records.automation.reason.${automation.reason}`, { defaultValue: automation.reason })
    : undefined;

  return (
    <span title={reason} className={cn(BADGE_CLS, cls, className)}>
      <Icon className={cn("h-3 w-3", spin && "animate-spin")} />
      {label}
    </span>
  );
}

/**
 * 자동 검수가 값을 고친 건 — 사람이 확인하기 전에는 "확인 권장 · 고친 칸", 확인하면 흐리게.
 * 마우스를 올리면 칸별 원래 값 → 고친 값과 확인자를 보여준다.
 */
function AutoFixBadge({
  automation,
  className,
}: {
  automation: AutomationState;
  className?: string;
}) {
  const { t } = useTranslation();
  const checked = !!automation.checked_at;
  const changes = autoFixChanges(automation);
  const empty = t("records.automation.autoFix.empty");
  const fieldName = (key: string) => {
    const meta = (FIELD_META_MAP as Record<string, FieldMeta | undefined>)[key];
    return meta ? t(meta.labelI18nKey) : key;
  };

  const lines = changes.map((c) =>
    t("records.automation.autoFix.change", {
      field: fieldName(c.key),
      before: c.before || empty,
      after: c.after || empty,
    }),
  );
  if (automation.rounds) lines.push(t("records.automation.rounds", { count: automation.rounds }));
  if (checked) {
    lines.push(
      t("records.automation.autoFix.checkedBy", {
        who: automation.checked_by?.email ?? "—",
        at: formatDateTime(automation.checked_at ?? null),
      }),
    );
  }

  const names = changes.map((c) => fieldName(c.key));
  const fields =
    names.length > 2
      ? t("records.automation.autoFix.fieldsMore", {
          fields: names.slice(0, 2).join(", "),
          count: names.length - 2,
        })
      : names.join(", ");
  const Icon = checked ? CheckCircle2 : Eye;

  return (
    <span
      title={lines.join("\n")}
      className={cn(
        BADGE_CLS,
        checked
          ? "border-slate-200 bg-slate-50 text-slate-500"
          : "border-amber-300 bg-amber-50 font-semibold text-amber-800",
        className,
      )}
    >
      <Icon className="h-3 w-3" />
      {checked ? t("records.automation.autoFix.checked") : t("records.automation.autoFix.pending")}
      {!checked && fields && ` · ${fields}`}
    </span>
  );
}
